import React, { useEffect, useState } from "react";
import SidebarLayout from "../../components/SidebarLayout";
import Modal from "../../components/Modal";
import { BACKEND_URL } from "../../config";

interface StockMedicine {
  id: number;
  name: string;
  brand?: string;
  manufacturer?: string;
  batchNo?: string;
  quantity: number;
  minimum_stock_count?: number;
  total_medicine_stock?: number;
  strip_count?: number;
  medicines_per_strip?: number;
  buyingPrice: number;
  mrpPrice: number;
  stock_value: number;
  sale_value: number;
  expiryDate?: string;
  status: "available" | "critical" | "out_of_stock";
}

interface StockHistoryRow {
  medicine_id: number;
  medicine_name: string;
  batchNo?: string;
  quantity_sold: number;
  current_quantity: number;
}

interface StockReportResponse {
  date_from: string;
  date_to: string;
  critical_threshold: number;
  summary: {
    total_medicines: number;
    available_count: number;
    critical_count: number;
    out_of_stock_count: number;
    total_units: number;
    stock_value: number;
    movement_count: number;
    units_sold: number;
  };
  critical_stocks: StockMedicine[];
  medicines: StockMedicine[];
  stock_history: StockHistoryRow[];
}

const today = () => new Date().toISOString().slice(0, 10);

const monthStart = () => {
  const date = new Date();
  date.setDate(1);
  return date.toISOString().slice(0, 10);
};

const money = (value?: number) => `Rs ${Number(value || 0).toFixed(2)}`;

const emptyReport: StockReportResponse = {
  date_from: today(),
  date_to: today(),
  critical_threshold: 10,
  summary: {
    total_medicines: 0,
    available_count: 0,
    critical_count: 0,
    out_of_stock_count: 0,
    total_units: 0,
    stock_value: 0,
    movement_count: 0,
    units_sold: 0,
  },
  critical_stocks: [],
  medicines: [],
  stock_history: [],
};

type StockTab = "critical" | "current" | "history";

const statusClass = (status: StockMedicine["status"]) => {
  if (status === "out_of_stock") return "bg-alert-red/10 text-alert-red";
  if (status === "critical") return "bg-warning-amber/15 text-warning-amber";
  return "bg-success-green/10 text-success-green";
};

const statusLabel = (status: StockMedicine["status"]) => {
  if (status === "out_of_stock") return "Out of stock";
  if (status === "critical") return "Critical";
  return "Available";
};

const StockReport: React.FC = () => {
  const [dateFrom, setDateFrom] = useState(monthStart());
  const [dateTo, setDateTo] = useState(today());
  const [criticalThreshold, setCriticalThreshold] = useState(10);
  const [report, setReport] = useState<StockReportResponse>(emptyReport);
  const [activeTab, setActiveTab] = useState<StockTab>("critical");
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState({ open: false, title: "", message: "" });

  const loadReport = async (from = dateFrom, to = dateTo, threshold = criticalThreshold) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        date_from: from,
        date_to: to,
        critical_threshold: String(threshold),
      });
      const res = await fetch(`${BACKEND_URL}/api/reports/stock?${params.toString()}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(await res.text());
      setReport(await res.json());
    } catch (err: any) {
      setModal({ open: true, title: "Unable to load stock report", message: err.message || "Please try again." });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReport(monthStart(), today(), 10);
  }, []);

  const showToday = () => {
    const value = today();
    setDateFrom(value);
    setDateTo(value);
    loadReport(value, value);
  };

  const showCurrentMonth = () => {
    const from = monthStart();
    const to = today();
    setDateFrom(from);
    setDateTo(to);
    loadReport(from, to);
  };

  const summaryCards = [
    { label: "Medicines", value: String(report.summary.total_medicines), tone: "text-primary" },
    { label: "Available", value: String(report.summary.available_count), tone: "text-success-green" },
    { label: "Critical", value: String(report.summary.critical_count), tone: "text-warning-amber" },
    { label: "Out of Stock", value: String(report.summary.out_of_stock_count), tone: "text-alert-red" },
    { label: "Units Sold", value: String(report.summary.units_sold), tone: "text-text-light" },
    { label: "Stock Value", value: money(report.summary.stock_value), tone: "text-primary" },
  ];

  const tabs: Array<{ key: StockTab; label: string; count: number }> = [
    { key: "critical", label: "Critically Low Stock", count: report.critical_stocks.length },
    { key: "current", label: "Current Stock", count: report.medicines.length },
    { key: "history", label: "Stock History", count: report.stock_history.length },
  ];

  return (
    <SidebarLayout title="Stock Report" subtitle="Review current medicine stock, critical levels, and sale-based stock history.">
      <Modal open={modal.open} title={modal.title} message={modal.message} onClose={() => setModal((prev) => ({ ...prev, open: false }))} />

      <section className="rounded border border-border-light bg-surface-light p-5">
        <div className="flex flex-col gap-3 xl:flex-row xl:items-end xl:justify-between">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <label>
              <div className="mb-1 text-sm font-semibold">From</div>
              <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-full rounded border p-3" />
            </label>
            <label>
              <div className="mb-1 text-sm font-semibold">To</div>
              <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-full rounded border p-3" />
            </label>
            <label>
              <div className="mb-1 text-sm font-semibold">Fallback Minimum</div>
              <input
                type="number"
                min="0"
                value={criticalThreshold}
                onChange={(e) => setCriticalThreshold(Number(e.target.value || 0))}
                className="w-full rounded border p-3"
              />
            </label>
          </div>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={showToday} className="rounded border border-border-light px-4 py-3 font-semibold text-subtle-light hover:bg-primary/10 hover:text-primary">
              Today
            </button>
            <button type="button" onClick={showCurrentMonth} className="rounded border border-border-light px-4 py-3 font-semibold text-subtle-light hover:bg-primary/10 hover:text-primary">
              Current Month
            </button>
            <button type="button" onClick={() => loadReport()} className="rounded bg-primary px-5 py-3 font-semibold text-white">
              Apply
            </button>
          </div>
        </div>
      </section>

      <section className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-6">
        {summaryCards.map((card) => (
          <div key={card.label} className="rounded border border-border-light bg-surface-light p-4">
            <div className="text-sm font-semibold text-subtext-light">{card.label}</div>
            <div className={`mt-2 text-2xl font-black ${card.tone}`}>{card.value}</div>
          </div>
        ))}
      </section>

      <section className="mt-5 rounded border border-border-light bg-surface-light p-5">
        <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap gap-2" role="tablist" aria-label="Stock report sections">
            {tabs.map((tab) => {
              const isActive = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setActiveTab(tab.key)}
                  className={`rounded border px-4 py-2 text-sm font-semibold transition ${
                    isActive
                      ? "border-primary bg-primary text-white"
                      : "border-border-light text-subtext-light hover:bg-primary/10 hover:text-primary"
                  }`}
                >
                  {tab.label}
                  <span className={`ml-2 rounded px-2 py-0.5 text-xs ${isActive ? "bg-white/20 text-white" : "bg-background-light text-subtext-light"}`}>
                    {tab.count}
                  </span>
                </button>
              );
            })}
          </div>
          <div className="text-sm text-subtext-light">
            {activeTab === "critical" && "Using configured minimum stock per medicine"}
            {activeTab === "current" && `${report.summary.total_units} units available`}
            {activeTab === "history" && `${report.date_from} to ${report.date_to}`}
          </div>
        </div>

        {loading ? (
          <div>Loading...</div>
        ) : (
          <>
            {activeTab === "critical" && (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[760px] text-left text-sm">
                  <thead className="bg-background-light text-xs uppercase text-subtext-light">
                    <tr>
                      <th className="p-2">Medicine</th>
                      <th className="p-2">Batch</th>
                      <th className="p-2 text-right">Stock</th>
                      <th className="p-2 text-right">Total</th>
                      <th className="p-2 text-right">Minimum</th>
                      <th className="p-2">Expiry</th>
                      <th className="p-2">Status</th>
                      <th className="p-2 text-right">Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.critical_stocks.map((medicine) => (
                      <tr key={medicine.id} className="border-t">
                        <td className="p-2">
                          <div className="font-semibold">{medicine.name}</div>
                          <div className="text-xs text-subtext-light">{medicine.brand || medicine.manufacturer || "-"}</div>
                        </td>
                        <td className="p-2">{medicine.batchNo || "-"}</td>
                        <td className="p-2 text-right font-bold">{medicine.quantity}</td>
                        <td className="p-2 text-right font-bold">{medicine.total_medicine_stock ?? medicine.quantity}</td>
                        <td className="p-2 text-right">{medicine.minimum_stock_count ?? report.critical_threshold}</td>
                        <td className="p-2">{medicine.expiryDate || "-"}</td>
                        <td className="p-2">
                          <span className={`rounded px-2 py-1 text-xs font-bold ${statusClass(medicine.status)}`}>{statusLabel(medicine.status)}</span>
                        </td>
                        <td className="p-2 text-right">{money(medicine.stock_value)}</td>
                      </tr>
                    ))}
                    {!report.critical_stocks.length && (
                      <tr>
                        <td colSpan={8} className="p-4 text-center text-subtext-light">No critically low medicines right now.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {activeTab === "current" && (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[760px] text-left text-sm">
                  <thead className="bg-background-light text-xs uppercase text-subtext-light">
                    <tr>
                      <th className="p-2">Medicine</th>
                      <th className="p-2">Batch</th>
                      <th className="p-2 text-right">Stock</th>
                      <th className="p-2 text-right">Minimum</th>
                      <th className="p-2 text-right">Buying</th>
                      <th className="p-2 text-right">MRP</th>
                      <th className="p-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.medicines.map((medicine) => (
                      <tr key={medicine.id} className="border-t">
                        <td className="p-2">
                          <div className="font-semibold">{medicine.name}</div>
                          <div className="text-xs text-subtext-light">{medicine.manufacturer || "-"}</div>
                        </td>
                        <td className="p-2">{medicine.batchNo || "-"}</td>
                        <td className="p-2 text-right font-bold">{medicine.quantity}</td>
                        <td className="p-2 text-right">{medicine.minimum_stock_count ?? report.critical_threshold}</td>
                        <td className="p-2 text-right">{money(medicine.buyingPrice)}</td>
                        <td className="p-2 text-right">{money(medicine.mrpPrice)}</td>
                        <td className="p-2">
                          <span className={`rounded px-2 py-1 text-xs font-bold ${statusClass(medicine.status)}`}>{statusLabel(medicine.status)}</span>
                        </td>
                      </tr>
                    ))}
                    {!report.medicines.length && (
                      <tr>
                        <td colSpan={7} className="p-4 text-center text-subtext-light">No medicines found.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {activeTab === "history" && (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[620px] text-left text-sm">
                  <thead className="bg-background-light text-xs uppercase text-subtext-light">
                    <tr>
                      <th className="p-2">Medicine</th>
                      <th className="p-2">Batch</th>
                      <th className="p-2 text-right">Sold</th>
                      <th className="p-2 text-right">Current Stock</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.stock_history.map((row) => (
                      <tr key={row.medicine_id} className="border-t">
                        <td className="p-2 font-semibold">{row.medicine_name}</td>
                        <td className="p-2">{row.batchNo || "-"}</td>
                        <td className="p-2 text-right font-bold text-alert-red">-{row.quantity_sold}</td>
                        <td className="p-2 text-right">{row.current_quantity}</td>
                      </tr>
                    ))}
                    {!report.stock_history.length && (
                      <tr>
                        <td colSpan={4} className="p-4 text-center text-subtext-light">No stock movement in this range.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </section>
    </SidebarLayout>
  );
};

export default StockReport;
